import {
  Menu,
  MenuItem,
  FormControlLabel,
  Divider,
  Grid2,
  IconButton,
  Tooltip,
  Checkbox,
  Collapse,
} from "@mui/material";
import MaterialBox from "../../../../components/MaterialBox";
import MaterialTypography from "../../../../components/MaterialTypography";
import { ExpandMore, FilterAltOff } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useLoading } from "../../../../components/Loading";
import { useSnackbar } from "../../../../context/popup";
import { fetchData } from "../../../../services";
import DropDownConfig from "../../../Utils/DropDownConfig";

const SchemasListFilterDialog = ({
  anchorEl,
  open,
  onClose,
  filterData,
  setFilterData,
  onApply,
}) => {
  const sectionHeader = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "2px 6px",
    background: "#e9ecf5",
    borderRadius: "6px",
    cursor: "pointer",
    mb: 0.5,
  };

  const menuItemStyle = {
    borderRadius: "6px",
    my: 0.2,
    mx: 0.5,
    py: 0.3,
    "&:hover": { backgroundColor: "#dfe6ff" },
  };

  const labelStyle = {
    m: 0,
    "& .MuiFormControlLabel-label": { fontSize: "0.8rem" },
  };

  const { setCircleLoading } = useLoading();
  const { showSnackbar } = useSnackbar();

  const [openActive, setOpenActive] = useState(false);
  const [openCity, setOpenCity] = useState(false);
  const [openRtoRegion, setOpenRtoRegion] = useState(false);
  const [openRtoZone, setOpenRtoZone] = useState(false);
  const [apiCategoryOptions, setApiCategoryOptions] = useState([]);
  const [schemaTypeOptions, setSchemaTypeOptions] = useState([]);
  const [compatibilityModeOptions, setCompatibilityModeOptions] = useState([]);
  
  useEffect(() => {
    const fetchDropdowns = async () => {
      setCircleLoading(true);
      try {
        const results = await Promise.allSettled([
          fetchData(
            `/api-engine/key-value-store?dropdown=true&isActive=true&code=${DropDownConfig?.apiApiCategory}`
          ),
          fetchData(
            `/api-engine/key-value-store?dropdown=true&isActive=true&code=${DropDownConfig?.apiSchemaType}`
          ),
          fetchData(
            `/api-engine/key-value-store?dropdown=true&isActive=true&code=${DropDownConfig?.apiCompatibilityMode}`
          ),
        ]);

        const [categoryResult, schemaTypeResult, compatibilityResult] = results;

        // API Category
        if (
          categoryResult.status === "fulfilled" &&
          categoryResult.value?.success
        ) {
          setApiCategoryOptions(
            categoryResult.value.data?.items?.map((item) => ({
              value: item.id,
              label: item.keyName,
            })) || []
          );
        }

        // Schema Type
        if (
          schemaTypeResult.status === "fulfilled" &&
          schemaTypeResult.value?.success
        ) {
          setSchemaTypeOptions(
            schemaTypeResult.value.data?.items?.map((item) => ({
              value: item.id,
              label: item.keyName,
            })) || []
          );
        }

        // Compatibility Mode
        if (
          compatibilityResult.status === "fulfilled" &&
          compatibilityResult.value?.success
        ) {
          setCompatibilityModeOptions(
            compatibilityResult.value.data?.items?.map((item) => ({
              value: item.id,
              label: item.keyName,
            })) || []
          );
        }

        // Optional global error message
        if (results.some((r) => r.status === "rejected")) {
          showSnackbar("Some dropdowns failed to load", "warning");
        }
      } catch (error) {
        console.error("Unexpected dropdown error:", error);
        showSnackbar("Error fetching Dropdowns", "warning");
      }finally{
        setCircleLoading(false);
      }
    };
    if(open === true){
      fetchDropdowns();
    }
    else{
      return
    }
  }, [open]);

  const handleChange = ({ target: { name, value } }) => {
    setFilterData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onClearFilters = () => {
    setFilterData({
      isActive: "",
      apiCategoryId: "",
      schemaTypeId: "",
      compatibilityModeId: ""
    });
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      sx={{ backgroundColor: "rgba(0, 0, 0, 0.3)" }}
      PaperProps={{
        elevation: 3,
        sx: {
          width: 240,
          padding: 1.5,
        },
      }}
    >
      <MaterialBox
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* Title */}
        <MaterialTypography variant="body2" fontWeight="bold">
          Motor RTO Filter
        </MaterialTypography>

        {/* Clear Button */}
        <Tooltip title="Clear Filter" disableInteractive>
          <IconButton
            size="small"
            onClick={onClearFilters}
            sx={{ color: "#ce0707ff" }}
          >
            <FilterAltOff />
          </IconButton>
        </Tooltip>
      </MaterialBox>

      {/* Content */}
      <MaterialBox
        ml={1}
        sx={{
          maxHeight: 300,
          overflowY: "auto",
        }}
      >
        <Grid2 container spacing={1}>
          <Grid2 size={{ xs: 12 }}>
            <MaterialBox>
              <MaterialBox
                sx={sectionHeader}
                onClick={() => setOpenActive(!openActive)}
              >
                <MaterialTypography variant="body3" fontWeight="bold">
                  Active Status
                </MaterialTypography>

                <IconButton size="small">
                  <ExpandMore
                    sx={{
                      transform: openActive ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "0.2s",
                    }}
                  />
                </IconButton>
              </MaterialBox>

              <Collapse in={openActive}>
                <MenuItem
                  disableRipple
                  sx={menuItemStyle}
                  onClick={() =>
                    handleChange({
                      target: { name: "isActive", value: true },
                    })
                  }
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={filterData.isActive === true}
                      />
                    }
                    label="Active"
                    sx={labelStyle}
                  />
                </MenuItem>

                <MenuItem
                  disableRipple
                  sx={menuItemStyle}
                  onClick={() =>
                    handleChange({
                      target: { name: "isActive", value: false },
                    })
                  }
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={filterData.isActive === false}
                      />
                    }
                    label="Inactive"
                    sx={labelStyle}
                  />
                </MenuItem>
              </Collapse>
            </MaterialBox>
          </Grid2>

          <Grid2 size={{ xs: 12 }}>
            <MaterialBox>
              <MaterialBox
                sx={sectionHeader}
                onClick={() => setOpenCity(!openCity)}
              >
                <MaterialTypography variant="body3" fontWeight="bold">
                  API Category
                </MaterialTypography>

                <IconButton size="small">
                  <ExpandMore
                    sx={{
                      transform: openCity ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "0.2s",
                    }}
                  />
                </IconButton>
              </MaterialBox>

              <Collapse in={openCity}>
                {apiCategoryOptions?.map((item) => (
                  <MenuItem
                    key={item.value}
                    disableRipple
                    sx={menuItemStyle}
                    onClick={() =>
                      handleChange({
                        target: { name: "apiCategoryId", value: item.value },
                      })
                    }
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={filterData.apiCategoryId === item.value}
                        />
                      }
                      label={item.label}
                      sx={labelStyle}
                    />
                  </MenuItem>
                ))}
              </Collapse>
            </MaterialBox>
          </Grid2>

          <Grid2 size={{ xs: 12 }}>
            <MaterialBox>
              <MaterialBox
                sx={sectionHeader}
                onClick={() => setOpenRtoRegion(!openRtoRegion)}
              >
                <MaterialTypography variant="body3" fontWeight="bold">
                  Schema Type
                </MaterialTypography>

                <IconButton size="small">
                  <ExpandMore
                    sx={{
                      transform: openRtoRegion
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "0.2s",
                    }}
                  />
                </IconButton>
              </MaterialBox>

              <Collapse in={openRtoRegion}>
                {schemaTypeOptions?.map((item) => (
                  <MenuItem
                    key={item.value}
                    disableRipple
                    sx={menuItemStyle}
                    onClick={() =>
                      handleChange({
                        target: { name: "schemaTypeId", value: item.value },
                      })
                    }
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={filterData.schemaTypeId === item.value}
                        />
                      }
                      label={item.label}
                      sx={labelStyle}
                    />
                  </MenuItem>
                ))}
              </Collapse>
            </MaterialBox>
          </Grid2>

          <Grid2 size={{ xs: 12 }}>
            <MaterialBox>
              <MaterialBox
                sx={sectionHeader}
                onClick={() => setOpenRtoZone(!openRtoZone)}
              >
                <MaterialTypography variant="body3" fontWeight="bold">
                  Compatibility Mode
                </MaterialTypography>

                <IconButton size="small">
                  <ExpandMore
                    sx={{
                      transform: openRtoZone
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "0.2s",
                    }}
                  />
                </IconButton>
              </MaterialBox>

              <Collapse in={openRtoZone}>
                {compatibilityModeOptions?.map((item) => (
                  <MenuItem
                    key={item.value}
                    disableRipple
                    sx={menuItemStyle}
                    onClick={() =>
                      handleChange({
                        target: {
                          name: "compatibilityModeId",
                          value: item.value,
                        },
                      })
                    }
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={
                            filterData.compatibilityModeId === item.value
                          }
                        />
                      }
                      label={item.label}
                      sx={labelStyle}
                    />
                  </MenuItem>
                ))}
              </Collapse>
            </MaterialBox>
          </Grid2>
        </Grid2>
      </MaterialBox>

      <Divider />

      {/* Apply button */}
      <MenuItem
        onClick={() => onApply()}
        disableRipple
        sx={{
          padding: "6px 16px",
          width: "fit-content",
          minWidth: "0px",
          mx: "auto",
          borderRadius: "6px",
          backgroundColor: "#bcc7e9",
          color: "white",
          display: "flex",
          justifyContent: "center",
          transition: "0.2s ease",

          "&:hover": {
            backgroundColor: "#93a6e8",
          },
        }}
      >
        <MaterialTypography variant="button" fontWeight="bold">
          APPLY FILTER
        </MaterialTypography>
      </MenuItem>
    </Menu>
  );
};

export default SchemasListFilterDialog;
